import { apiClient, type ApiResponse } from './client'

export interface CurrentBookView {
  bookId: number
  title: string
  volumeLabel: string | null
  materialType: string
  coverImageUrl: string | null
  totalLessons: number
  currentLessonRank: number
  lastVolume: boolean
}

export interface TextbookAlertView {
  reason: 'INITIAL_BOOK' | 'NEXT_VOLUME' | 'MANUAL'
  printBookId: number
  bookTitle: string
  volumeLabel: string | null
  materialType: string
  sourceBookId: number | null
  triggerLessonId: number
  requestable: boolean
  blockedReason: string | null
  gracePeriod: boolean
  onLeave: boolean
  existingRequestId: number | null
  existingRequestStatus: string | null
}

export interface TextbookRequestView {
  id: number
  learnerUserId: number
  printBookId: number
  bookTitle: string | null
  volumeLabel: string | null
  materialType: string | null
  requestReason: 'INITIAL_BOOK' | 'NEXT_VOLUME' | 'MANUAL'
  status:
    | 'REQUESTED_TO_HQ'
    | 'OUTBOUND_REQUESTING'
    | 'OUTBOUND_REQUESTED'
    | 'OUTBOUND_UNCERTAIN'
    | 'SHIPPED'
    | 'RECEIVED'
    | 'CANCELLED'
    | 'REJECTED'
  inGracePeriod: boolean
  requestedBy: number
  requestedAt: string
  statusChangedBy: number | null
  statusChangedAt: string | null
  memo: string | null
  statusMessage: string | null
}

export interface StudentTextbookStatusResponse {
  curriculumId: number | null
  contentFeeBlockedReason: string | null
  currentBooks: CurrentBookView[]
  alerts: TextbookAlertView[]
  requests: TextbookRequestView[]
}

export interface CreateTextbookRequestBody {
  printBookId: number
  requestAttemptId: string
  memo: string | null
}

export interface UpdateTextbookRequestStatusBody {
  status: 'RECEIVED' | 'CANCELLED'
  expectedStatus: TextbookRequestView['status']
  attemptId: string
  reason: string | null
}

export interface TextbookAlertSummaryView {
  userId: number
  alertCount: number
  activeRequestCount: number
}

export interface LcTextbookCatalogView {
  bookId: number
  title: string
  volumeLabel: string | null
  materialType: string
  scopeLevel: string | null
  scopeContentId: number | null
  nextBookId: number | null
  totalLessons: number
  coverImageUrl: string | null
}

export const textbookRequestApi = {
  getStudentStatus: (userId: number) =>
    apiClient.get<ApiResponse<StudentTextbookStatusResponse>>(
      `/lc/admin/students/${userId}/textbook-status`,
    ),
  create: (userId: number, body: CreateTextbookRequestBody) =>
    apiClient.post<ApiResponse<TextbookRequestView>>(
      `/lc/admin/students/${userId}/textbook-requests`,
      body,
    ),
  updateStatus: (requestId: number, body: UpdateTextbookRequestStatusBody) =>
    apiClient.patch<ApiResponse<TextbookRequestView>>(
      `/lc/admin/textbook-requests/${requestId}`,
      body,
    ),
  getAlerts: () =>
    apiClient.get<ApiResponse<TextbookAlertSummaryView[]>>('/lc/admin/textbook-requests/alerts'),
  getCatalog: (curriculumId: number) =>
    apiClient.get<ApiResponse<LcTextbookCatalogView[]>>('/lc/admin/textbooks', {
      params: { curriculumId },
    }),
}
